import { useNavigate } from '@tanstack/react-router';
import { FC, useMemo } from 'react';
import { EntityLink } from '../entity-link';
import { EntityLinkSize, EntityLinkVariant, computeIconSize } from '../entity-link/helpers';
import { AgentMeta } from '@/lib/models/agent.model';
import { createUseStyles } from 'react-jss';
import { Avatar, Theme } from '@mui/material';
import { getInitials } from '@/lib/helpers';
import classNames from 'classnames';

interface Props {
  agent: AgentMeta;
  size?: EntityLinkSize;
  variant?: EntityLinkVariant;
  canNavigate?: boolean;
  showAdminName?: boolean;
  condensed?: boolean;
}

const useStyles = createUseStyles<string, { avatarSize: number }>((theme: Theme) => ({
  image: {
    borderRadius: theme.shape.borderRadius,
  },
  avatar: {
    backgroundColor: '#7ebd94',
    width: ({ avatarSize }) => avatarSize,
    height: ({ avatarSize }) => avatarSize,
    fontSize: ({ avatarSize }) => avatarSize / 2,
    '&.unavailable': {
      backgroundColor: theme.palette.gray,
    },
  },
}));

const AgentLink: FC<Props> = ({
  agent,
  size = 'medium',
  variant = 'text',
  canNavigate = false,
  showAdminName = false,
  condensed = false,
}) => {
  const navigate = useNavigate();
  const avatarSize = useMemo(() => computeIconSize(size, false), [size]);
  const styles = useStyles({ avatarSize });

  // Handle undefined agent (unavailable state)
  if (!agent) {
    return (
      <EntityLink
        title="Unavailable Assistant"
        image={<Avatar className={classNames(styles.avatar, 'unavailable')} />}
        size={size}
        variant={variant}
        iconSize={avatarSize}
        condensed={condensed}
      />
    );
  }

  const { id, name, portal, portalTitle } = agent;
  const { image } = portal.badge;
  const hasImage = !!image;

  const onClick = () => {
    navigate({ to: `/config/assistants/$agentId`, params: { agentId: id } });
  };

  // Determine image/avatar to display
  const imageElement = hasImage ? (
    <img
      src={image}
      alt={portalTitle}
      width={avatarSize}
      height={avatarSize}
      className={styles.image}
    />
  ) : (
    <Avatar className={styles.avatar}>{getInitials(portalTitle)}</Avatar>
  );

  return (
    <EntityLink
      title={portalTitle}
      subtext={showAdminName ? name : undefined}
      onClick={canNavigate ? onClick : undefined}
      image={imageElement}
      size={size}
      variant={variant}
      iconSize={avatarSize}
    />
  );
};

export default AgentLink;
